import { Schema } from "effect";
import {
  AccountId,
  ChannelThreadId,
  ShopperAiIntegrationId,
} from "../../../ids/definitions";
import { DateFromDb } from "../../../schema/helpers";
import type { ShopperAiIntegrationType } from "../integration/integration";
import {
  type IntegrationKind,
  IntegrationKindSchema,
  kindsOfIntegrationTypes,
} from "./channel-utils";
import { MessageChannel } from "./message";
import { ConversationProduct } from "./product";
import {
  AppleMessagesThreadChannelData,
  ChatFlowThreadChannelData,
  EmailThreadChannelData,
  MetaCommentThreadChannelData,
  MobileMessageThreadChannelData,
  RedoChatThreadChannelData,
  SmsThreadChannelData,
  ThreadAnalyticsData,
  VoiceThreadChannelData,
} from "./thread/channel-data";

const threadBaseFields = {
  id: ChannelThreadId,
  // Null when the thread's lane resolves to no integration at all.
  integrationId: Schema.NullOr(ShopperAiIntegrationId),
  accountId: AccountId,
  externalThreadId: Schema.String,
  channel: MessageChannel,
  products: Schema.Array(ConversationProduct),
  // DateFromDb accepts both an ISO string (fresh off the wire) and a Date
  // instance (already-decoded), so the collection's schema re-validation on
  // writeUpsert doesn't reject the client-side value.
  createdAt: DateFromDb,
  analyticsData: Schema.NullOr(ThreadAnalyticsData),
};

/** The integration types behind each thread shape (its `channelData`). */
export const THREAD_SHAPE_TYPES = {
  email: ["gmail", "outlook", "custom_email"],
  metaComment: ["facebook", "instagram"],
  chatFlow: ["redoChatFlow"],
  redoChat: ["redoChatAi", "redoChatLive"],
  sms: ["telnyx_sms", "imessage", "sms_phone_number", "shared_sms", "rcs"],
  appleMessages: ["apple_messages"],
  mobileMessage: ["mobileMessage"],
  voice: ["telnyx_voice"],
  noChannelData: [
    "tiktok",
    "attentive",
    "postscript",
    "contactForm",
    "gorgias_sms",
    "richpanel_sms",
  ],
} as const satisfies Record<string, readonly ShopperAiIntegrationType[]>;
export type ThreadShape = keyof typeof THREAD_SHAPE_TYPES;

const { noChannelData: _noChannelDataTypes, ...SHAPES_WITH_CHANNEL_DATA } =
  THREAD_SHAPE_TYPES;
const KINDS_WITH_CHANNEL_DATA = kindsOfIntegrationTypes(
  Object.values(SHAPES_WITH_CHANNEL_DATA).flat(),
);

/** Whether a thread decodes as `shape`: by its integration type, or with no
 * integration by its lane kind. Kinds no data shape owns have no channel data. */
export const threadHasShape = (
  thread: {
    readonly integrationType: string | null;
    readonly kind?: IntegrationKind | null;
  },
  shape: ThreadShape,
): boolean => {
  const types: readonly string[] = THREAD_SHAPE_TYPES[shape];
  if (thread.integrationType !== null) {
    return types.includes(thread.integrationType);
  }
  if (shape === "noChannelData") {
    return (
      thread.kind == null || !KINDS_WITH_CHANNEL_DATA.includes(thread.kind)
    );
  }
  return (
    thread.kind != null &&
    kindsOfIntegrationTypes(THREAD_SHAPE_TYPES[shape]).includes(thread.kind)
  );
};

const kind = Schema.optional(Schema.NullOr(IntegrationKindSchema));

interface ShapeFields {
  readonly integrationType: string | null;
  readonly kind?: IntegrationKind | null;
}

/** Narrows a thread schema to threads of `shape`; see {@link threadHasShape}. */
export const ofShape =
  (shape: ThreadShape) =>
  <A extends ShapeFields, I, R>(self: Schema.Schema<A, I, R>) =>
    self.pipe(
      Schema.filter((thread) => threadHasShape(thread, shape), {
        message: () => `thread is not a ${shape} thread`,
      }),
    );

const EmailChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(Schema.Literal(...THREAD_SHAPE_TYPES.email)),
  kind,
  channelData: Schema.NullOr(EmailThreadChannelData),
}).pipe(ofShape("email"));

const MetaCommentChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.metaComment),
  ),
  kind,
  channelData: Schema.NullOr(MetaCommentThreadChannelData),
}).pipe(ofShape("metaComment"));

const ChatFlowChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.chatFlow),
  ),
  kind,
  channelData: Schema.NullOr(ChatFlowThreadChannelData),
}).pipe(ofShape("chatFlow"));

const RedoChatChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.redoChat),
  ),
  kind,
  channelData: Schema.NullOr(RedoChatThreadChannelData),
}).pipe(ofShape("redoChat"));

const SmsChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(Schema.Literal(...THREAD_SHAPE_TYPES.sms)),
  kind,
  channelData: Schema.NullOr(SmsThreadChannelData),
}).pipe(ofShape("sms"));

const AppleMessagesChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.appleMessages),
  ),
  kind,
  channelData: Schema.NullOr(AppleMessagesThreadChannelData),
}).pipe(ofShape("appleMessages"));

const MobileMessageChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.mobileMessage),
  ),
  kind,
  channel: Schema.Literal("mobileMessage"),
  channelData: Schema.NullOr(MobileMessageThreadChannelData),
}).pipe(ofShape("mobileMessage"));

const VoiceChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(Schema.Literal(...THREAD_SHAPE_TYPES.voice)),
  kind,
  channelData: Schema.NullOr(VoiceThreadChannelData),
}).pipe(ofShape("voice"));

const NullChannelChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.NullOr(
    Schema.Literal(...THREAD_SHAPE_TYPES.noChannelData),
  ),
  kind,
  channelData: Schema.Null,
}).pipe(ofShape("noChannelData"));

export const ChannelThread = Schema.Union(
  EmailChannelThread,
  MetaCommentChannelThread,
  ChatFlowChannelThread,
  RedoChatChannelThread,
  SmsChannelThread,
  AppleMessagesChannelThread,
  MobileMessageChannelThread,
  VoiceChannelThread,
  NullChannelChannelThread,
);
export type ChannelThread = typeof ChannelThread.Type;

export const ChannelThreadCursor = Schema.Struct({
  id: ChannelThreadId,
  createdAt: DateFromDb,
});
export type ChannelThreadCursor = typeof ChannelThreadCursor.Type;
